import BadgeStrapi, {
  BadgeStrapiProps,
} from '@/decodo/components/atoms/badge/BadgeStrapi';
import {
  ImageStrapiProps,
  transformCMSAssetToNextImageProps,
} from '@/decodo/components/atoms/Image/ImageStrapi';
import {
  LinkButtonStrapiProps,
  transformFromCMSLink,
} from '@/decodo/components/atoms/LinkButton/LinkStrapi';
import { PricePerProps } from '@/decodo/features/Header/PricePer';

import { badgeInnerProps as headerItemBadgeProps } from './components/HeaderItem';
import { MenuCarouselProps } from './components/MenuCarousel';
import { badgeInnerProps as menuItemBadgeProps } from './components/MenuItem';
import { HeaderProps } from './Header';

type ImageTextLinkStrapiProps = {
  image?: ImageStrapiProps;
  text?: LinkButtonStrapiProps;
  link: LinkButtonStrapiProps;
};

type StrapiMenuCarousel = {
  title?: string;
  images: ImageTextLinkStrapiProps[];
};

type HeaderThreeLevel = {
  level?: string;
  title?: string;
  description?: string;
  link: LinkButtonStrapiProps;
  badge?: BadgeStrapiProps;
  price?: PricePerProps;
  image?: ImageStrapiProps;
  showArrow: boolean;
  highlighted?: boolean;
};

type HeaderPromoBanner = {
  title?: string;
  description?: string;
  link?: LinkButtonStrapiProps;
  image?: ImageStrapiProps;
};

type HeaderTwoLevel = {
  level?: string;
  title?: string;
  badge?: BadgeStrapiProps;
  link?: LinkButtonStrapiProps;
  hideTitleOnMobile?: boolean;
  hideSectionOnMobile?: boolean;
  thirdLevels?: HeaderThreeLevel[];
};

type HeaderOneLevel = {
  level?: string;
  title?: string;
  badge?: BadgeStrapiProps;
  secondLevels?: HeaderTwoLevel[];
  extraGroup?: {
    title?: string;
    list?: HeaderThreeLevel[];
    link?: LinkButtonStrapiProps;
  }[];
  carousel?: StrapiMenuCarousel;
  promoBanners?: HeaderPromoBanner[];
};

export type HeaderStrapiProps = {
  logo?: ImageStrapiProps;
  logoLink?: string;
  header?: {
    firstLevels?: HeaderOneLevel[];
  };
  extraSectionLinks?: LinkButtonStrapiProps[];
  showLanguages?: boolean;
  logoAlignment?: string;
  documentId?: string;
};

export function transformMainLinkMenuListItems(
  data: HeaderThreeLevel[],
  inExtraGroup?: boolean
): HeaderProps['mainLinks'][number]['menu'][number]['lists'][number]['items'] {
  return data.map((datum) => ({
    icon: datum.image
      ? transformCMSAssetToNextImageProps(datum.image)
      : undefined,
    title: datum.title ?? '',
    description: datum.description,
    showChevron: datum.showArrow,
    url: datum.link ? transformFromCMSLink(datum.link).href : '',
    badge: datum.badge ? (
      <BadgeStrapi
        block={{ text: datum.badge.text }}
        inner={menuItemBadgeProps}
      />
    ) : undefined,
    price: datum.price ? datum.price.price : undefined,
    priceUnit: datum.price ? datum.price.pricePer : undefined,
    highlighted: datum.highlighted,
    inExtraGroup,
  }));
}

function assertExtraGroup(
  type: 'main' | 'extra',
  data: HeaderOneLevel['extraGroup'] | HeaderTwoLevel[]
): data is HeaderOneLevel['extraGroup'] {
  return !!data && type === 'extra' && data.length !== undefined;
}

function transformMenuCarousel(
  carousel: StrapiMenuCarousel
): MenuCarouselProps & { title: string } {
  const MAX_CAROUSEL_ITEMS = 3;
  return {
    title: carousel.title ?? '',
    items: carousel.images
      .filter((imageLink) => imageLink.image !== undefined)
      .slice(0, MAX_CAROUSEL_ITEMS)
      .map((imageLink) => ({
        image: transformCMSAssetToNextImageProps(imageLink.image!),
        link: transformFromCMSLink(imageLink.link),
      })),
  };
}

const transformSuffixMenuItem = (link: LinkButtonStrapiProps) => ({
  title: link.text ?? 'View all',
  url: link.url,
  showChevron: true,
});

export function transformMainLinkMenuGroup(
  type: 'main' | 'extra',
  data: HeaderTwoLevel[] | HeaderOneLevel['extraGroup'],
  carousel?: HeaderOneLevel['carousel'],
  promoBanners?: HeaderOneLevel['promoBanners']
): HeaderProps['mainLinks'][number]['menu'][number] {
  const isExtraInfo = assertExtraGroup(type, data);

  if (isExtraInfo) {
    if (!data) return { type, lists: [] };

    return {
      type,
      lists: data
        .filter((list) => !!list.list?.length)
        .map((datum) => ({
          title: datum.title ?? '',
          link: datum.link ? transformSuffixMenuItem(datum.link) : undefined,
          items: transformMainLinkMenuListItems(datum.list ?? [], true),
        })),
      carousel: carousel ? transformMenuCarousel(carousel) : undefined,
    };
  }

  if (!data) return { type, lists: [] };
  return {
    type,
    promoBanners: promoBanners?.map((banner) => ({
      title: banner.title ?? '',
      description: banner.description,
      url: banner.link?.url ?? '',
      image: banner.image
        ? transformCMSAssetToNextImageProps(banner.image)
        : undefined,
    })),
    lists: data
      .filter((list) => !!list.thirdLevels)
      .map(({ thirdLevels, ...secondLevel }) => ({
        title: secondLevel.title ?? '',
        link: secondLevel.link
          ? transformSuffixMenuItem(secondLevel.link)
          : undefined,
        items: transformMainLinkMenuListItems(thirdLevels ?? []),
        hideTitleOnMobile: secondLevel.hideTitleOnMobile,
        hideSectionOnMobile: secondLevel.hideSectionOnMobile,
      })),
  };
}

export function transformMainLinkMenu(
  data: HeaderOneLevel
): HeaderProps['mainLinks'][number]['menu'] {
  const { secondLevels: mainGroup, extraGroup, carousel, promoBanners } = data;

  return [
    transformMainLinkMenuGroup(
      'main',
      mainGroup ?? [],
      undefined,
      promoBanners
    ),
    transformMainLinkMenuGroup('extra', extraGroup ?? [], carousel),
  ];
}

export function transformMainLinkItem(
  data: HeaderOneLevel
): HeaderProps['mainLinks'][number]['item'] | undefined {
  if (!data.title) return;

  const badge = data.badge ? (
    <BadgeStrapi
      block={{ text: data.badge.text }}
      inner={headerItemBadgeProps}
    />
  ) : null;

  return { ...data, badge };
}

export function transformMainLinks(
  mainLinks: HeaderOneLevel[]
): HeaderProps['mainLinks'] {
  return mainLinks
    .map((link) => {
      const { secondLevels: mainGroup, extraGroup, ...mainLink } = link;

      const mainLinkItem = transformMainLinkItem(mainLink);
      if (!mainLinkItem) return;

      return {
        item: mainLinkItem,
        menu: transformMainLinkMenu(link),
      };
    })
    .filter((link) => link !== undefined) as HeaderProps['mainLinks'];
}

export function transformStrapiHeader(data: HeaderStrapiProps): HeaderProps {
  const { header, extraSectionLinks, logo, logoLink, showLanguages } = data;

  return {
    logoImage: logo ? transformCMSAssetToNextImageProps(logo) : undefined,
    logoLink: logoLink ?? '',
    mainLinks: transformMainLinks(header?.firstLevels ?? []),
    extraLinks: extraSectionLinks ?? [],
    isRegionVisible: showLanguages ?? false,
  };
}
